// The live queue's call state, Available → Ringing → On call → Wrap-up → Available. In phase 1 the
// agent drives it by hand; once the phone system reports call events, the same actions come from it.

export type LiveCallPhase = "available" | "ringing" | "onCall" | "wrapUp";

export interface LiveCallState {
  phase: LiveCallPhase;
  /** When the agent pressed Call (ISO). Sent with the outcome as the call's start. */
  startedAt: string | null;
  /** When the customer answered (ISO). The on-call timer runs from here. */
  answeredAt: string | null;
  endedAt: string | null;
}

export type LiveCallAction =
  | { type: "dial"; at: string }
  | { type: "answered"; at: string }
  | { type: "end"; at: string }
  | { type: "reset" };

export const initialLiveCall: LiveCallState = { phase: "available", startedAt: null, answeredAt: null, endedAt: null };

export function liveCallReducer(state: LiveCallState, action: LiveCallAction): LiveCallState {
  switch (action.type) {
    case "dial":
      if (state.phase !== "available") return state;
      return { phase: "ringing", startedAt: action.at, answeredAt: null, endedAt: null };
    case "answered":
      if (state.phase !== "ringing") return state;
      return { ...state, phase: "onCall", answeredAt: action.at };
    case "end":
      if (state.phase !== "ringing" && state.phase !== "onCall") return state;
      return { ...state, phase: "wrapUp", endedAt: action.at };
    case "reset":
      return initialLiveCall;
    default:
      return state;
  }
}

/** Seconds on the call: from answer to now, or to the end once it's over. */
export function callSeconds(state: LiveCallState, now: Date): number {
  if (!state.answeredAt) return 0;
  const end = state.endedAt ? new Date(state.endedAt) : now;
  return Math.max(0, Math.floor((end.getTime() - new Date(state.answeredAt).getTime()) / 1000));
}

/** mm:ss, or h:mm:ss past the hour. */
export function formatCallTime(totalSeconds: number): string {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const mm = String(m).padStart(2, "0");
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}
